import { useState } from "react";
import { type Stakeholder, type RaciEntry, type RaciType } from "@/lib/projectStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, X, Mail, Phone, Building2, Pencil, Check } from "lucide-react";

const ROLE_PRESETS = [
  "Business Owner",
  "Product Owner",
  "Principal Architect",
  "Technical Project Manager",
  "Scrum Master",
  "BA Lead",
  "Developer",
  "QA Engineer",
  "UX Designer",
  "Compliance Officer",
  "External Vendor",
] as const;

const TEAM_PRESETS = [
  "Business",
  "Engineering",
  "QA / Testing",
  "Architecture",
  "Product",
  "Compliance",
  "Offshore",
  "External Vendor",
] as const;

const roleColors: Record<string, string> = {
  "Business Owner": "bg-accent/15 text-accent border-accent/30",
  "Product Owner": "bg-accent/15 text-accent border-accent/30",
  "Principal Architect": "bg-primary/15 text-primary border-primary/30",
  "Technical Project Manager": "bg-primary/15 text-primary border-primary/30",
  "Scrum Master": "bg-destructive/15 text-destructive border-destructive/30",
  "BA Lead": "bg-destructive/15 text-destructive border-destructive/30",
  Developer: "bg-secondary text-secondary-foreground border-border",
  "QA Engineer": "bg-secondary text-secondary-foreground border-border",
};

const RACI_OPTIONS: { value: RaciType; label: string; color: string }[] = [
  { value: "R", label: "R", color: "bg-accent text-accent-foreground" },
  { value: "A", label: "A", color: "bg-destructive text-destructive-foreground" },
  { value: "C", label: "C", color: "bg-primary text-primary-foreground" },
  { value: "I", label: "I", color: "bg-secondary text-secondary-foreground" },
  { value: "", label: "–", color: "bg-muted text-muted-foreground" },
];

interface Props {
  stakeholders: Stakeholder[];
  onChange: (stakeholders: Stakeholder[]) => void;
  raciMatrix: RaciEntry[];
  onRaciChange: (raci: RaciEntry[]) => void;
}

export default function StakeholderPanel({ stakeholders, onChange, raciMatrix, onRaciChange }: Props) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [newActivity, setNewActivity] = useState("");

  function emptyForm() {
    return { name: "", role: "", team: "", email: "", phone: "", timezone: "", notes: "" };
  }

  const handleAdd = () => {
    if (!form.name.trim() || !form.role) return;
    const stakeholder: Stakeholder = {
      id: crypto.randomUUID(),
      name: form.name.trim().slice(0, 100),
      role: form.role,
      team: form.team,
      email: form.email.trim().slice(0, 255),
      phone: form.phone.trim().slice(0, 30),
      timezone: form.timezone.trim().slice(0, 50),
      notes: form.notes.trim().slice(0, 500),
    };
    onChange([...stakeholders, stakeholder]);
    setForm(emptyForm());
    setOpen(false);
  };

  const startEdit = (person: Stakeholder) => {
    setEditingId(person.id);
    setForm({
      name: person.name,
      role: person.role,
      team: person.team,
      email: person.email || "",
      phone: person.phone || "",
      timezone: person.timezone || "",
      notes: person.notes || "",
    });
  };

  const saveEdit = (id: string) => {
    if (!form.name.trim() || !form.role) return;
    onChange(
      stakeholders.map((s) =>
        s.id === id
          ? {
              ...s,
              name: form.name.trim().slice(0, 100),
              role: form.role,
              team: form.team,
              email: form.email.trim().slice(0, 255),
              phone: form.phone.trim().slice(0, 30),
              timezone: form.timezone.trim().slice(0, 50),
              notes: form.notes.trim().slice(0, 500),
            }
          : s
      )
    );
    setEditingId(null);
    setForm(emptyForm());
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const removeStakeholder = (id: string) => {
    onChange(stakeholders.filter((s) => s.id !== id));
    // Clean up RACI assignments
    onRaciChange(
      raciMatrix.map((r) => {
        const { [id]: _, ...rest } = r.assignments;
        return { ...r, assignments: rest };
      })
    );
  };

  const addRaciActivity = () => {
    if (!newActivity.trim()) return;
    const entry: RaciEntry = {
      id: crypto.randomUUID(),
      activity: newActivity.trim().slice(0, 200),
      assignments: {},
    };
    onRaciChange([...raciMatrix, entry]);
    setNewActivity("");
  };

  const updateRaci = (entryId: string, stakeholderId: string, value: RaciType) => {
    onRaciChange(
      raciMatrix.map((r) =>
        r.id === entryId
          ? { ...r, assignments: { ...r.assignments, [stakeholderId]: value } }
          : r
      )
    );
  };

  const removeRaciActivity = (entryId: string) => {
    onRaciChange(raciMatrix.filter((r) => r.id !== entryId));
  };

  // Group by team
  const teams = new Map<string, Stakeholder[]>();
  stakeholders.forEach((s) => {
    const team = s.team || "Unassigned";
    if (!teams.has(team)) teams.set(team, []);
    teams.get(team)!.push(s);
  });

  return (
    <div className="p-4 space-y-4">
      <Tabs defaultValue="people">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="people" className="text-xs">People</TabsTrigger>
            <TabsTrigger value="raci" className="text-xs">RACI Matrix</TabsTrigger>
          </TabsList>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add Person
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">Add Stakeholder</DialogTitle>
              </DialogHeader>
              <StakeholderForm form={form} setForm={setForm} onSubmit={handleAdd} submitLabel="Add Stakeholder" />
            </DialogContent>
          </Dialog>
        </div>

        <TabsContent value="people" className="mt-0 space-y-6">
          {stakeholders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No stakeholders added yet. Track business owners, architects, developers, and scrum masters.
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {stakeholders.length} people · {teams.size} teams
              </p>
              {Array.from(teams.entries()).map(([teamName, members]) => (
                <div key={teamName}>
                  <h3 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Building2 className="h-3 w-3" />
                    {teamName} ({members.length})
                  </h3>
                  <div className="space-y-2">
                    {members.map((person) =>
                      editingId === person.id ? (
                        <div key={person.id} className="border-2 border-accent rounded-md p-3 bg-card space-y-2">
                          <StakeholderForm
                            form={form}
                            setForm={setForm}
                            onSubmit={() => saveEdit(person.id)}
                            submitLabel="Save"
                            onCancel={cancelEdit}
                            inline
                          />
                        </div>
                      ) : (
                        <div
                          key={person.id}
                          className="border border-border rounded-md p-3 bg-card flex items-start gap-3 group"
                        >
                          <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-secondary-foreground shrink-0 mt-0.5">
                            {person.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-card-foreground font-body">
                                {person.name}
                              </span>
                              <Badge
                                variant="outline"
                                className={`text-[10px] px-1.5 py-0 ${roleColors[person.role] || "bg-muted text-muted-foreground border-border"}`}
                              >
                                {person.role}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                              {person.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" /> {person.email}
                                </span>
                              )}
                              {person.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" /> {person.phone}
                                </span>
                              )}
                              {person.timezone && (
                                <span className="text-[10px] font-mono bg-secondary/50 px-1.5 py-0.5 rounded">
                                  {person.timezone}
                                </span>
                              )}
                            </div>
                            {person.notes && (
                              <p className="text-[11px] text-muted-foreground mt-1 italic">{person.notes}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => startEdit(person)} className="text-muted-foreground hover:text-accent p-1">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => removeStakeholder(person.id)} className="text-muted-foreground hover:text-destructive p-1">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="raci" className="mt-0">
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground">
              {RACI_OPTIONS.filter((o) => o.value).map((o) => (
                <span key={o.value} className="flex items-center gap-1">
                  <span className={`inline-block w-5 h-5 rounded text-center leading-5 text-[10px] font-bold ${o.color}`}>
                    {o.label}
                  </span>
                  {o.value === "R" && "Responsible"}
                  {o.value === "A" && "Accountable"}
                  {o.value === "C" && "Consulted"}
                  {o.value === "I" && "Informed"}
                </span>
              ))}
            </div>

            {stakeholders.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Add stakeholders first to build a RACI matrix.
              </div>
            ) : (
              <>
                <div className="border border-border rounded-md overflow-x-auto">
                  <table className="w-full text-xs font-body">
                    <thead className="bg-secondary/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-mono text-muted-foreground uppercase tracking-wider min-w-[180px] sticky left-0 bg-secondary/50">
                          Activity
                        </th>
                        {stakeholders.map((s) => (
                          <th key={s.id} className="px-2 py-2 text-center min-w-[70px]">
                            <div className="font-semibold text-card-foreground truncate max-w-[80px]" title={s.name}>
                              {s.name.split(" ")[0]}
                            </div>
                            <div className="text-[9px] text-muted-foreground truncate">{s.role}</div>
                          </th>
                        ))}
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {raciMatrix.length === 0 ? (
                        <tr>
                          <td
                            colSpan={stakeholders.length + 2}
                            className="px-3 py-6 text-center text-muted-foreground"
                          >
                            Add activities below to build your RACI matrix
                          </td>
                        </tr>
                      ) : (
                        raciMatrix.map((entry) => (
                          <tr key={entry.id} className="hover:bg-secondary/20">
                            <td className="px-3 py-2 font-semibold text-card-foreground sticky left-0 bg-card">
                              {entry.activity}
                            </td>
                            {stakeholders.map((s) => {
                              const val = entry.assignments[s.id] || "";
                              const opt = RACI_OPTIONS.find((o) => o.value === val);
                              return (
                                <td key={s.id} className="px-2 py-2 text-center">
                                  <button
                                    onClick={() => {
                                      const cycle: RaciType[] = ["R", "A", "C", "I", ""];
                                      const idx = cycle.indexOf(val);
                                      updateRaci(entry.id, s.id, cycle[(idx + 1) % cycle.length]);
                                    }}
                                    className={`inline-block w-6 h-6 rounded text-[10px] font-bold transition-colors ${
                                      opt?.color || "bg-muted/50 text-muted-foreground/30"
                                    } hover:ring-2 hover:ring-accent/30`}
                                    title={`Click to cycle: R→A→C→I→empty`}
                                  >
                                    {val || "·"}
                                  </button>
                                </td>
                              );
                            })}
                            <td className="px-1 py-2">
                              <button
                                onClick={() => removeRaciActivity(entry.id)}
                                className="text-muted-foreground hover:text-destructive p-1"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-2">
                  <Input
                    placeholder="Add activity (e.g., Gather requirements, Sprint planning, UAT sign-off…)"
                    value={newActivity}
                    onChange={(e) => setNewActivity(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addRaciActivity()}
                    className="text-xs"
                    maxLength={200}
                  />
                  <Button size="sm" onClick={addRaciActivity} disabled={!newActivity.trim()} className="gap-1">
                    <Plus className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* Shared form used in both add dialog and inline edit */
function StakeholderForm({
  form,
  setForm,
  onSubmit,
  submitLabel,
  onCancel,
  inline,
}: {
  form: { name: string; role: string; team: string; email: string; phone: string; timezone: string; notes: string };
  setForm: (f: any) => void;
  onSubmit: () => void;
  submitLabel: string;
  onCancel?: () => void;
  inline?: boolean;
}) {
  return (
    <div className={inline ? "space-y-2" : "space-y-3 pt-2"}>
      <Input
        placeholder="Full name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        maxLength={100}
        className={inline ? "h-8 text-xs" : ""}
      />
      <div className="grid grid-cols-2 gap-2">
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger className={inline ? "h-8 text-xs" : ""}>
            <SelectValue placeholder="Role…" />
          </SelectTrigger>
          <SelectContent>
            {ROLE_PRESETS.map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={form.team} onValueChange={(v) => setForm({ ...form, team: v })}>
          <SelectTrigger className={inline ? "h-8 text-xs" : ""}>
            <SelectValue placeholder="Team…" />
          </SelectTrigger>
          <SelectContent>
            {TEAM_PRESETS.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          maxLength={255}
          className={inline ? "h-8 text-xs" : ""}
        />
        <Input
          placeholder="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          maxLength={30}
          className={inline ? "h-8 text-xs" : ""}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          placeholder="Timezone (EST, PST…)"
          value={form.timezone}
          onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          maxLength={50}
          className={inline ? "h-8 text-xs" : ""}
        />
        <Input
          placeholder="Notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          maxLength={500}
          className={inline ? "h-8 text-xs" : ""}
        />
      </div>
      <div className="flex gap-2">
        <Button
          onClick={onSubmit}
          className={inline ? "flex-1 h-8 text-xs" : "w-full"}
          disabled={!form.name.trim() || !form.role}
          size={inline ? "sm" : "default"}
        >
          {inline && <Check className="h-3 w-3 mr-1" />}
          {submitLabel}
        </Button>
        {onCancel && (
          <Button variant="outline" onClick={onCancel} size={inline ? "sm" : "default"} className={inline ? "h-8 text-xs" : ""}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
